// EAIRI — identity.
//
// Passwordless email sign-in against Supabase Auth. No password to store, to
// leak, or to reset, and the audience — an executive who buys one diagnostic a
// quarter — is exactly the audience for whom a password is friction they will
// forget by the next visit.
//
// The handoff had no identity at all: purchases, runs and documents lived in a
// browser's localStorage, so clearing site data destroyed an 890 EUR purchase.
// Ownership is now server-held and durable; this module only proves who is
// asking.

import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";

let client: SupabaseClient | null = null;

export interface AuthConfig {
  readonly url: string;
  readonly anonKey: string;
}

export function authConfig(): AuthConfig | null {
  const env = import.meta.env as Record<string, string | undefined>;
  const url = (env.VITE_SUPABASE_URL ?? "").trim();
  const anonKey = (env.VITE_SUPABASE_ANON_KEY ?? "").trim();
  return url && anonKey ? { url, anonKey } : null;
}

export function supabase(): SupabaseClient | null {
  if (client) return client;
  const cfg = authConfig();
  if (!cfg) return null;
  client = createClient(cfg.url, cfg.anonKey, {
    auth: {
      // The session must survive a redirect back from Stripe, so it is
      // persisted and refreshed rather than held in memory.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  });
  return client;
}

/** Send the sign-in link. Deliberately reports the same thing whether or not
    the address is already known — a sign-in form that distinguishes them is an
    account-enumeration oracle. */
export async function sendSignInLink(email: string, redirectTo: string): Promise<{ sent: boolean }> {
  const sb = supabase();
  if (!sb) return { sent: false };
  await sb.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo } });
  return { sent: true };
}

export async function currentSession(): Promise<Session | null> {
  const sb = supabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session ?? null;
}

/** The access token, refreshed if needed. Passed to EairiClient.getToken so
    every call carries a live credential rather than a stale one. */
export async function accessToken(): Promise<string | null> {
  const s = await currentSession();
  return s?.access_token ?? null;
}

export async function signOut(): Promise<void> {
  await supabase()?.auth.signOut();
}

export function onAuthChange(fn: (session: Session | null) => void): () => void {
  const sb = supabase();
  if (!sb) return () => {};
  const { data } = sb.auth.onAuthStateChange((_e, session) => fn(session));
  return () => data.subscription.unsubscribe();
}
